import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { PageBlocks } from 'constants/app';
import { BETSLIP_LINE_INT_ODDS, BETSLIP_LINE_INT_US_ODDS } from 'constants/betslip';
import { mobileBetslip as branding } from 'constants/branding';
import { InlinePlacementActions } from 'constants/inlinePlacement';
import { BettingTypes } from 'constants/markets';
import { BetSides } from 'constants/myBets';
import { useBetslipLabels } from 'hooks/useLabels';
import { useIsMobilePlacementDisabled } from 'hooks/useMobilePlacement';
import useOddsType from 'hooks/useOddsType';
import { getPNCEnabledSetting } from 'redux/modules/appConfigs/selectors';
import { getCurrentGameName, getIsCurrentGame } from 'redux/modules/games/selectors';
import { setIsMobilePlacementConfirmationStep } from 'redux/modules/inlinePlacement';
import { getIsMobilePlacementConfirmationStep } from 'redux/modules/inlinePlacement/selectors';
import { TInlineSelectedBet } from 'redux/modules/inlinePlacement/type';
import { getMarketDataById } from 'redux/modules/placement/selectors';
import { PageBlock } from 'types';
import { TLabels } from 'types/betslip';

import styles from './styles.module.scss';

type MobileBetslipHeaderProps = {
  bet: TInlineSelectedBet;
  removeBet: (isManualClicked: boolean) => void;
  pageBlock?: PageBlock;
};

const MobileBetslipHeader = ({ bet, removeBet, pageBlock = PageBlocks.HOME }: MobileBetslipHeaderProps) => {
  const dispatch = useDispatch();

  const isGame = pageBlock === PageBlocks.GAME;
  const isConfirmationStep = useSelector(getIsMobilePlacementConfirmationStep);
  const isCurrentGameData = useSelector(getIsCurrentGame);
  const currentGameName = useSelector(getCurrentGameName);
  const isPNCEnabled = useSelector(getPNCEnabledSetting);
  const market = useSelector(getMarketDataById(pageBlock, bet.marketId, bet.sportId));
  const marketName = isGame && isCurrentGameData ? currentGameName : market?.marketName ?? '';
  const selectionName =
    bet.selectionName ||
    (market?.runners?.find(runner => runner.selectionId === bet.selectionId && runner.handicap === bet.handicap)
      ?.runnerName ??
      '');

  const { isDisabled } = useIsMobilePlacementDisabled(bet);
  const { isAmericanOdds } = useOddsType();

  const labels: TLabels = useBetslipLabels({
    eventTypeId: market?.eventType?.id ?? '',
    bettingType: market?.description?.bettingType ?? '',
    marketType: market?.description?.marketType ?? '',
    marketUnit: market?.description?.lineRangeInfo?.marketUnit ?? null
  });

  const bettingType = market?.description.bettingType;
  const isLineMarket = bettingType === BettingTypes.LINE;

  const onClickHeaderBtn = () => {
    if (isConfirmationStep) {
      dispatch(setIsMobilePlacementConfirmationStep(false));
    } else {
      removeBet(true);
    }
  };

  return (
    <div
      className={classNames(
        styles.header,
        {
          [styles.header__back]: bet.type === BetSides.Back,
          [styles.header__lay]: bet.type === BetSides.Lay,
          [styles.header__unmatched]: bet.action === InlinePlacementActions.EDIT,
          [styles.header__disabled]: isDisabled && bet.action !== InlinePlacementActions.EDIT,
          [branding.UNMATCHED]: bet.action === InlinePlacementActions.EDIT
        },
        branding.BET_HEADER
      )}
    >
      <p
        className={classNames(styles.header__title, {
          [styles.header__title__unmatched]: bet.action === InlinePlacementActions.EDIT
        })}
      >
        {labels[bet.type]?.bet}:
        <span className={styles.header__title__selection}>
          {selectionName}
          {isLineMarket && !isPNCEnabled
            ? ` @${isAmericanOdds ? BETSLIP_LINE_INT_US_ODDS : BETSLIP_LINE_INT_ODDS}`
            : ''}
        </span>{' '}
        - {marketName}
      </p>
      <button className={styles.header__close__btn} onClick={onClickHeaderBtn}>
        {isConfirmationStep ? (
          <i className={classNames('biab_custom-icon-edit', styles.header__edit)} />
        ) : (
          <i
            className={classNames('biab_custom-icon-close', styles.header__close, {
              [styles.header__close__unmatched]: bet.action === InlinePlacementActions.EDIT
            })}
          />
        )}
      </button>
    </div>
  );
};

export default MobileBetslipHeader;
